import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Capacitor } from '@capacitor/core';
import { getPlayerId } from '../utils';
import {
  cancelNativeNotifications,
  getNativePermission,
  isNativeNotificationsSupported,
  requestNativePermission,
  scheduleNativeNotification,
  showNativeNotification,
} from "../nativeNotifications";
import {
  isNativePushSupported,
  isWebPushSupported,
  registerPush,
  syncPushPreferences,
  unregisterPush,
  type PushPreferencesPayload,
} from "../pushNotifications";

// ============================================
// PUSH NOTIFICATIONS SYSTEM
// ============================================

export type NotificationType = 
  | 'daily_reward'
  | 'energy_full'
  | 'friend_online'
  | 'friend_request'
  | 'clan_war'
  | 'tournament_starting'
  | 'tournament_result'
  | 'battle_pass_reward'
  | 'achievement_unlocked'
  | 'shop_offer'
  | 'limited_event'
  | 'streak_reminder'
  | 'inactivity'
  | 'custom';

export interface NotificationConfig {
  type: NotificationType;
  enabled: boolean;
  title: string;
  description: string;
  icon: string;
  defaultTime?: string; // HH:MM format
  sound: boolean;
  vibrate: boolean;
  priority: 'low' | 'normal' | 'high';
}

export interface ScheduledNotification {
  id: string;
  type: NotificationType;
  title: string;
  body: string;
  scheduledFor: number; // timestamp
  data?: Record<string, unknown>;
  sent: boolean;
  clicked: boolean;
}

export interface InAppNotification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  icon: string;
  timestamp: number;
  read: boolean;
  action?: {
    type: 'navigate' | 'open_modal' | 'external';
    target: string;
  };
  expiresAt?: number;
}

// ============================================
// NOTIFICATION CONFIGS
// ============================================

export const NOTIFICATION_CONFIGS: NotificationConfig[] = [
  {
    type: 'daily_reward',
    enabled: true,
    title: 'Recompensa Diária',
    description: 'Lembrete para coletar sua recompensa diária',
    icon: '🎁',
    defaultTime: '09:00',
    sound: true,
    vibrate: true,
    priority: 'high',
  },
  {
    type: 'energy_full',
    enabled: true,
    title: 'Energia Cheia',
    description: 'Aviso quando sua energia estiver totalmente recuperada',
    icon: '⚡',
    sound: true,
    vibrate: false,
    priority: 'normal',
  },
  {
    type: 'friend_online',
    enabled: true,
    title: 'Amigo Online',
    description: 'Quando um amigo favorito entrar no jogo',
    icon: '👥',
    sound: false,
    vibrate: false,
    priority: 'low',
  },
  {
    type: 'friend_request',
    enabled: true,
    title: 'Pedido de Amizade',
    description: 'Quando alguém te adicionar como amigo',
    icon: '💌',
    sound: true,
    vibrate: true,
    priority: 'normal',
  },
  {
    type: 'clan_war',
    enabled: true,
    title: 'Guerra de Clã',
    description: 'Início e fim de guerras de clã',
    icon: '⚔️',
    sound: true,
    vibrate: true,
    priority: 'high',
  },
  {
    type: 'tournament_starting',
    enabled: true,
    title: 'Torneio Começando',
    description: '15 minutos antes de um torneio inscrito',
    icon: '🏆',
    sound: true,
    vibrate: true,
    priority: 'high',
  },
  {
    type: 'tournament_result',
    enabled: true,
    title: 'Resultado do Torneio',
    description: 'Quando um torneio que você participou terminar',
    icon: '🥇',
    sound: true,
    vibrate: false,
    priority: 'normal',
  },
  {
    type: 'battle_pass_reward',
    enabled: true,
    title: 'Recompensa do Battle Pass',
    description: 'Quando subir de nível no Battle Pass',
    icon: '🎖️',
    sound: true,
    vibrate: false,
    priority: 'normal',
  },
  {
    type: 'achievement_unlocked',
    enabled: true,
    title: 'Conquista Desbloqueada',
    description: 'Quando desbloquear uma nova conquista',
    icon: '🏅',
    sound: true,
    vibrate: true,
    priority: 'normal',
  },
  {
    type: 'shop_offer',
    enabled: false,
    title: 'Ofertas da Loja',
    description: 'Ofertas especiais e descontos',
    icon: '🛍️',
    sound: false,
    vibrate: false,
    priority: 'low',
  },
  {
    type: 'limited_event',
    enabled: true,
    title: 'Evento Limitado',
    description: 'Início de eventos especiais por tempo limitado',
    icon: '🎉',
    sound: true,
    vibrate: true,
    priority: 'high',
  },
  {
    type: 'streak_reminder',
    enabled: true,
    title: 'Lembrete de Streak',
    description: 'Não perca seu streak de login diário!',
    icon: '🔥',
    defaultTime: '20:00',
    sound: true,
    vibrate: true,
    priority: 'high',
  },
  {
    type: 'inactivity',
    enabled: false,
    title: 'Saudades!',
    description: 'Lembrete após alguns dias sem jogar',
    icon: '💔',
    sound: false,
    vibrate: false,
    priority: 'low',
  },
];

// ============================================
// STATE INTERFACE
// ============================================

interface NotificationsState {
  // Permission status
  permissionStatus: 'default' | 'granted' | 'denied' | 'unsupported';
  /**
   * Identificador da inscrição de push remoto no servidor: `endpoint` da
   * subscription no web, token do FCM no Android. Guardado para poder
   * cancelar e para atualizar preferências sem reinscrever.
   */
  pushEndpoint: string | null;

  // Preferences
  preferences: Record<NotificationType, boolean>;
  quietHours: {
    enabled: boolean;
    start: string; // HH:MM
    end: string;   // HH:MM
  };
  soundEnabled: boolean;
  vibrateEnabled: boolean;
  
  // Scheduled notifications
  scheduledNotifications: ScheduledNotification[];
  
  // In-app notifications
  inAppNotifications: InAppNotification[];
  unreadCount: number;
  
  // UI state
  showNotificationCenter: boolean;
  showPermissionPrompt: boolean;
  
  // Permission & subscription
  requestPermission: () => Promise<boolean>;
  subscribeToPush: () => Promise<boolean>;
  unsubscribeFromPush: () => Promise<void>;
  
  // Preferences
  setPreference: (type: NotificationType, enabled: boolean) => void;
  setQuietHours: (enabled: boolean, start?: string, end?: string) => void;
  toggleSound: (enabled: boolean) => void;
  toggleVibrate: (enabled: boolean) => void;
  
  // Scheduling
  scheduleNotification: (notification: Omit<ScheduledNotification, 'id' | 'sent' | 'clicked'>) => string;
  cancelNotification: (id: string) => void;
  cancelAllOfType: (type: NotificationType) => void;
  checkScheduledNotifications: () => void;
  
  // In-app notifications
  addInAppNotification: (notification: Omit<InAppNotification, 'id' | 'timestamp' | 'read'>) => string;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  deleteNotification: (id: string) => void;
  clearOldNotifications: () => void;
  
  // Quick notifications
  notifyDailyReward: () => void;
  notifyAchievement: (achievementName: string) => void;
  notifyFriendOnline: (friendName: string) => void;
  notifyTournament: (tournamentName: string, startsIn: number) => void;
  notifyBattlePassLevel: (level: number) => void;
  notifyStreakReminder: () => void;
  
  // UI
  openNotificationCenter: () => void;
  closeNotificationCenter: () => void;
  dismissPermissionPrompt: () => void;
  
  // Helpers
  isInQuietHours: () => boolean;
  canSendNotification: (type: NotificationType) => boolean;
  getUnreadNotifications: () => InAppNotification[];
  
  // Reset
  reset: () => void;
}

// ============================================
// HELPERS
// ============================================

const generateId = () => `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const checkBrowserSupport = (): boolean => {
  // Browser Notification API nao funciona no WebView do Capacitor
  if (Capacitor.isNativePlatform()) return false;
  return 'Notification' in window && 'serviceWorker' in navigator;
};

const getPermissionStatus = (): 'default' | 'granted' | 'denied' | 'unsupported' => {
  // App nativo: quem responde é o plugin de Local Notifications, e só de forma
  // assíncrona (o ajuste vem no bloco de inicialização, no fim do arquivo).
  // Antes só se perguntava pelo caminho do navegador, o app respondia
  // 'unsupported' e `canSendNotification()` barrava TUDO — nem o aviso do
  // sistema, nem a entrada no centro de notificações dentro do jogo.
  // 'default' é o estado honesto até a resposta chegar.
  if (isNativeNotificationsSupported()) return 'default';
  if (!checkBrowserSupport()) return 'unsupported';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (window as any).Notification.permission;
};

const sendSystemNotification = (title: string, options?: { body?: string; icon?: string; badge?: string; tag?: string; data?: unknown }) => {
  if (isNativeNotificationsSupported()) {
    void showNativeNotification(title, {
      body: options?.body,
      tag: options?.tag,
      data: options?.data,
    });
    return;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const NotificationAPI = (window as any).Notification;
  if (!NotificationAPI || NotificationAPI.permission !== 'granted') return;

  try {
    new NotificationAPI(title, {
      icon: '/icons/icon-192.png',
      badge: '/icons/icon-72.png',
      ...options,
    });
  } catch {
    // Service worker notification fallback
    navigator.serviceWorker.ready.then((registration) => {
      registration.showNotification(title, options);
    });
  }
};

/**
 * Cópia das preferências que o servidor precisa para filtrar antes de gastar
 * um envio. Sem isso o push remoto passaria por cima do que o jogador
 * desligou na tela de notificações — o aviso local respeita, o remoto não
 * respeitaria.
 */
const buildPushPreferences = (state: {
  preferences: Record<NotificationType, boolean>;
  quietHours: { enabled: boolean; start: string; end: string };
}): PushPreferencesPayload => ({
  types: { ...state.preferences },
  quietHours: { ...state.quietHours },
  tzOffsetMinutes: new Date().getTimezoneOffset(),
});

const initializePreferences = (): Record<NotificationType, boolean> => {
  const prefs: Record<string, boolean> = {};
  for (const config of NOTIFICATION_CONFIGS) {
    prefs[config.type] = config.enabled;
  }
  return prefs as Record<NotificationType, boolean>;
};

// ============================================
// STORE
// ============================================

export const useNotifications = create<NotificationsState>()(
  persist(
    (set, get) => ({
      // Initial state
      permissionStatus: getPermissionStatus(),
      pushEndpoint: null,

      preferences: initializePreferences(),
      quietHours: {
        enabled: false,
        start: '22:00',
        end: '08:00',
      },
      soundEnabled: true,
      vibrateEnabled: true,
      
      scheduledNotifications: [],
      inAppNotifications: [],
      unreadCount: 0,
      
      showNotificationCenter: false,
      showPermissionPrompt: false,
      
      // ============================================
      // PERMISSION & SUBSCRIPTION
      // ============================================
      
      requestPermission: async () => {
        // App nativo: permissão do Android (POST_NOTIFICATIONS no 13+).
        if (isNativeNotificationsSupported()) {
          const status = await requestNativePermission();
          set({ permissionStatus: status, showPermissionPrompt: false });
          // A mesma permissão vale para o aviso local e para o push do
          // servidor; concedida, registra no FCM na sequência.
          if (status === 'granted') await get().subscribeToPush();
          return status === 'granted';
        }

        if (!checkBrowserSupport()) {
          set({ permissionStatus: 'unsupported' });
          return false;
        }

        try {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const permission = await (window as any).Notification.requestPermission();
          set({ 
            permissionStatus: permission,
            showPermissionPrompt: false,
          });
          
          if (permission === 'granted') {
            await get().subscribeToPush();
          }
          
          return permission === 'granted';
        } catch {
          return false;
        }
      },
      
      /**
       * Inscreve no push REMOTO (iniciado pelo servidor). No navegador é Web
       * Push com VAPID; no app é o token do FCM. Os avisos locais — que o
       * próprio aparelho agenda — não passam por aqui e continuam funcionando
       * mesmo sem push remoto configurado no servidor.
       */
      subscribeToPush: async () => {
        if (!isNativePushSupported() && !isWebPushSupported()) return false;

        const endpoint = await registerPush(buildPushPreferences(get()));
        set({ pushEndpoint: endpoint });
        return endpoint !== null;
      },

      unsubscribeFromPush: async () => {
        // `unregisterPush` cuida da diferença entre as plataformas: no app não
        // existe service worker (esperar por `serviceWorker.ready` ficaria
        // pendurado para sempre), no web não existe plugin do FCM.
        await unregisterPush(get().pushEndpoint);
        set({ pushEndpoint: null });
      },

      // ============================================
      // PREFERENCES
      // ============================================
      
      setPreference: (type, enabled) => {
        set((state) => ({
          preferences: {
            ...state.preferences,
            [type]: enabled,
          },
        }));
        // O servidor filtra o push remoto pela cópia que tem; sem reenviar,
        // um tipo desligado agora continuaria chegando pelo caminho remoto.
        void syncPushPreferences(get().pushEndpoint, buildPushPreferences(get()));
      },

      setQuietHours: (enabled, start, end) => {
        set((state) => ({
          quietHours: {
            enabled,
            start: start || state.quietHours.start,
            end: end || state.quietHours.end,
          },
        }));
        void syncPushPreferences(get().pushEndpoint, buildPushPreferences(get()));
      },
      
      toggleSound: (enabled) => {
        set({ soundEnabled: enabled });
      },
      
      toggleVibrate: (enabled) => {
        set({ vibrateEnabled: enabled });
      },
      
      // ============================================
      // SCHEDULING
      // ============================================
      
      scheduleNotification: (notification) => {
        const id = generateId();

        set((state) => ({
          scheduledNotifications: [
            ...state.scheduledNotifications,
            { ...notification, id, sent: false, clicked: false },
          ],
        }));

        // No app, entrega o agendamento ao Android: ele dispara mesmo com o
        // jogo fechado. O caminho web depende de checkScheduledNotifications()
        // rodar, ou seja, do jogo estar aberto na hora marcada — que é
        // exatamente quando um lembrete de streak não serve para nada.
        if (isNativeNotificationsSupported() && get().canSendNotification(notification.type)) {
          void scheduleNativeNotification({
            id,
            title: notification.title,
            body: notification.body,
            at: notification.scheduledFor,
            data: notification.data,
          });
        }

        return id;
      },

      cancelNotification: (id) => {
        void cancelNativeNotifications([id]);
        set((state) => ({
          scheduledNotifications: state.scheduledNotifications.filter(n => n.id !== id),
        }));
      },

      cancelAllOfType: (type) => {
        const affected = get()
          .scheduledNotifications.filter(n => n.type === type)
          .map(n => n.id);
        void cancelNativeNotifications(affected);
        set((state) => ({
          scheduledNotifications: state.scheduledNotifications.filter(n => n.type !== type),
        }));
      },
      
      checkScheduledNotifications: () => {
        const state = get();
        const now = Date.now();
        
        const toSend = state.scheduledNotifications.filter(
          n => !n.sent && n.scheduledFor <= now
        );
        
        // No nativo o Android já disparou o agendamento entregue em
        // scheduleNotification(); reenviar aqui duplicaria o aviso. Só
        // marcamos como enviado, mantendo o histórico consistente.
        const alreadyDeliveredByOS = isNativeNotificationsSupported();

        for (const notification of toSend) {
          if (!alreadyDeliveredByOS && state.canSendNotification(notification.type)) {
            sendSystemNotification(notification.title, {
              body: notification.body,
              data: notification.data,
              tag: notification.type,
            });
          }
        }
        
        // Marcar como enviadas
        if (toSend.length > 0) {
          set((s) => ({
            scheduledNotifications: s.scheduledNotifications.map(n =>
              toSend.some(t => t.id === n.id) ? { ...n, sent: true } : n
            ),
          }));
        }
        
        // Limpar notificações antigas (mais de 24h)
        set((s) => ({
          scheduledNotifications: s.scheduledNotifications.filter(
            n => !n.sent || now - n.scheduledFor < 24 * 60 * 60 * 1000
          ),
        }));
      },
      
      // ============================================
      // IN-APP NOTIFICATIONS
      // ============================================
      
      addInAppNotification: (notification) => {
        const id = generateId();
        
        set((state) => ({
          inAppNotifications: [
            {
              ...notification,
              id,
              timestamp: Date.now(),
              read: false,
            },
            ...state.inAppNotifications,
          ].slice(0, 50), // Manter no máximo 50 notificações
          unreadCount: state.unreadCount + 1,
        }));
        
        return id;
      },
      
      markAsRead: (id) => {
        set((state) => {
          const notification = state.inAppNotifications.find(n => n.id === id);
          if (!notification || notification.read) return state;
          
          return {
            inAppNotifications: state.inAppNotifications.map(n =>
              n.id === id ? { ...n, read: true } : n
            ),
            unreadCount: Math.max(0, state.unreadCount - 1),
          };
        });
      },
      
      markAllAsRead: () => {
        set((state) => ({
          inAppNotifications: state.inAppNotifications.map(n => ({ ...n, read: true })),
          unreadCount: 0,
        }));
      },
      
      deleteNotification: (id) => {
        set((state) => {
          const notification = state.inAppNotifications.find(n => n.id === id);
          const wasUnread = notification && !notification.read;
          
          return {
            inAppNotifications: state.inAppNotifications.filter(n => n.id !== id),
            unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
          };
        });
      },
      
      clearOldNotifications: () => {
        const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
        
        set((state) => ({
          inAppNotifications: state.inAppNotifications.filter(
            n => n.timestamp > oneWeekAgo || !n.read
          ),
        }));
      },
      
      // ============================================
      // QUICK NOTIFICATIONS
      // ============================================
      
      notifyDailyReward: () => {
        const state = get();
        if (!state.canSendNotification('daily_reward')) return;
        
        state.addInAppNotification({
          type: 'daily_reward',
          title: '🎁 Recompensa Disponível!',
          message: 'Sua recompensa diária está esperando! Colete agora.',
          icon: '🎁',
          action: { type: 'open_modal', target: 'daily_rewards' },
        });
        
        sendSystemNotification('🎁 Recompensa Disponível!', {
          body: 'Sua recompensa diária está esperando!',
          tag: 'daily_reward',
        });
      },
      
      notifyAchievement: (achievementName) => {
        const state = get();
        if (!state.canSendNotification('achievement_unlocked')) return;
        
        state.addInAppNotification({
          type: 'achievement_unlocked',
          title: '🏅 Conquista Desbloqueada!',
          message: `Você desbloqueou "${achievementName}"!`,
          icon: '🏅',
          action: { type: 'open_modal', target: 'achievements' },
        });
        
        sendSystemNotification('🏅 Conquista Desbloqueada!', {
          body: `Você desbloqueou "${achievementName}"!`,
          tag: 'achievement',
        });
      },
      
      notifyFriendOnline: (friendName) => {
        const state = get();
        if (!state.canSendNotification('friend_online')) return;
        
        state.addInAppNotification({
          type: 'friend_online',
          title: '👥 Amigo Online',
          message: `${friendName} entrou no jogo!`,
          icon: '👥',
          action: { type: 'navigate', target: '/friends' },
          expiresAt: Date.now() + 5 * 60 * 1000, // 5 minutos
        });
      },
      
      notifyTournament: (tournamentName, startsIn) => {
        const state = get();
        if (!state.canSendNotification('tournament_starting')) return;
        
        const minutes = Math.ceil(startsIn / 60000);
        
        state.addInAppNotification({
          type: 'tournament_starting',
          title: '🏆 Torneio Começando!',
          message: `${tournamentName} começa em ${minutes} minutos!`,
          icon: '🏆',
          action: { type: 'navigate', target: '/tournaments' },
        });
        
        sendSystemNotification('🏆 Torneio Começando!', {
          body: `${tournamentName} começa em ${minutes} minutos!`,
          tag: 'tournament',
        });
      },
      
      notifyBattlePassLevel: (level) => {
        const state = get();
        if (!state.canSendNotification('battle_pass_reward')) return;
        
        state.addInAppNotification({
          type: 'battle_pass_reward',
          title: '🎖️ Novo Nível!',
          message: `Você atingiu o nível ${level} do Battle Pass!`,
          icon: '🎖️',
          action: { type: 'navigate', target: '/battle-pass' },
        });
      },
      
      notifyStreakReminder: () => {
        const state = get();
        if (!state.canSendNotification('streak_reminder')) return;
        
        // Agendar para o horário configurado
        const now = new Date();
        const [hours, minutes] = (
          NOTIFICATION_CONFIGS.find(c => c.type === 'streak_reminder')?.defaultTime || '20:00'
        ).split(':').map(Number);
        
        const scheduledTime = new Date();
        scheduledTime.setHours(hours, minutes, 0, 0);
        
        // Se já passou, agendar para amanhã
        if (scheduledTime <= now) {
          scheduledTime.setDate(scheduledTime.getDate() + 1);
        }
        
        state.scheduleNotification({
          type: 'streak_reminder',
          title: '🔥 Não Perca seu Streak!',
          body: 'Você ainda não jogou hoje. Mantenha seu streak!',
          scheduledFor: scheduledTime.getTime(),
        });
      },
      
      // ============================================
      // UI
      // ============================================
      
      openNotificationCenter: () => {
        set({ showNotificationCenter: true });
      },
      
      closeNotificationCenter: () => {
        set({ showNotificationCenter: false });
      },
      
      dismissPermissionPrompt: () => {
        set({ showPermissionPrompt: false });
      },
      
      // ============================================
      // HELPERS
      // ============================================
      
      isInQuietHours: () => {
        const state = get();
        if (!state.quietHours.enabled) return false;
        
        const now = new Date();
        const currentTime = now.getHours() * 60 + now.getMinutes();
        
        const [startH, startM] = state.quietHours.start.split(':').map(Number);
        const [endH, endM] = state.quietHours.end.split(':').map(Number);
        
        const startTime = startH * 60 + startM;
        const endTime = endH * 60 + endM;
        
        // Quiet hours pode atravessar a meia-noite
        if (startTime > endTime) {
          return currentTime >= startTime || currentTime < endTime;
        }
        
        return currentTime >= startTime && currentTime < endTime;
      },
      
      canSendNotification: (type) => {
        const state = get();
        
        // Verificar se está habilitado
        if (!state.preferences[type]) return false;
        
        // Verificar horário silencioso
        if (state.isInQuietHours()) return false;
        
        // Verificar permissão
        if (state.permissionStatus !== 'granted') return false;
        
        return true;
      },
      
      getUnreadNotifications: () => {
        return get().inAppNotifications.filter(n => !n.read);
      },
      
      // ============================================
      // RESET
      // ============================================
      
      reset: () => {
        set({
          preferences: initializePreferences(),
          quietHours: { enabled: false, start: '22:00', end: '08:00' },
          soundEnabled: true,
          vibrateEnabled: true,
          scheduledNotifications: [],
          inAppNotifications: [],
          unreadCount: 0,
          showNotificationCenter: false,
          showPermissionPrompt: false,
        });
      },
    }),
    {
      name: `rocketanks-notifications-${getPlayerId()}`,
      partialize: (state) => ({
        pushEndpoint: state.pushEndpoint,
        preferences: state.preferences,
        quietHours: state.quietHours,
        soundEnabled: state.soundEnabled,
        vibrateEnabled: state.vibrateEnabled,
        scheduledNotifications: state.scheduledNotifications.filter(n => !n.sent),
        inAppNotifications: state.inAppNotifications.slice(0, 20),
      }),
    }
  )
);

// ============================================
// HOOKS
// ============================================

export function useUnreadCount() {
  return useNotifications((state) => state.unreadCount);
}

export function useNotificationPreference(type: NotificationType) {
  return useNotifications((state) => state.preferences[type]);
}

export function useNotificationPermission() {
  return useNotifications((state) => ({
    status: state.permissionStatus,
    canRequest: state.permissionStatus === 'default',
    isGranted: state.permissionStatus === 'granted',
  }));
}

export function useRecentNotifications(count = 5) {
  return useNotifications((state) => 
    state.inAppNotifications.slice(0, count)
  );
}

// ============================================
// INITIALIZATION
// ============================================

// Verificar notificações agendadas periodicamente
if (typeof window !== 'undefined') {
  // No app nativo a permissão só pode ser consultada de forma assíncrona, mas o
  // estado inicial do store precisa ser síncrono. Ele nasce 'default' e é
  // corrigido aqui — sem isto, quem já concedeu a permissão em outra sessão
  // apareceria como "não concedida" e `canSendNotification()` barraria tudo,
  // inclusive o centro de notificações do jogo.
  if (isNativeNotificationsSupported()) {
    void getNativePermission().then((status) => {
      useNotifications.setState({ permissionStatus: status });
      // Permissão já concedida em outra sessão: reregistra o token do FCM. O
      // token pode ter sido rotacionado pelo Android (reinstalação, limpeza
      // de dados) e o servidor precisa do atual, senão o push some sem aviso.
      if (status === 'granted') void useNotifications.getState().subscribeToPush();
    });
  }

  // Push chegando com o jogo ABERTO: nesse caso o sistema não desenha nada
  // (Android) e o service worker não é o dono da tela (web) — quem mostra é o
  // centro de notificações do jogo.
  window.addEventListener('push:received', (event) => {
    const detail = (event as CustomEvent).detail as {
      title?: string;
      body?: string;
      data?: Record<string, unknown>;
    };
    const type = (detail?.data?.type as NotificationType) || 'custom';
    const state = useNotifications.getState();
    if (!state.canSendNotification(type)) return;

    state.addInAppNotification({
      type,
      title: detail?.title || 'Rocketanks',
      message: detail?.body || '',
      icon: NOTIFICATION_CONFIGS.find((c) => c.type === type)?.icon || '🔔',
    });
  });

  // Jogador tocou no aviso: abre o centro de notificações (a rota do payload
  // é tratada pelo roteador quando existe).
  window.addEventListener('push:opened', () => {
    useNotifications.getState().openNotificationCenter();
  });

  setInterval(() => {
    useNotifications.getState().checkScheduledNotifications();
  }, 60000); // A cada minuto
  
  // Limpar notificações antigas
  setInterval(() => {
    useNotifications.getState().clearOldNotifications();
  }, 24 * 60 * 60 * 1000); // A cada 24 horas
}
